import React, { useState } from "react";
import { invoiceRepo, slipRepo } from "../../services";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Badge, Button, DataTable, Modal, NotConnected, PageHead, Select, StatCard, StatusBadge, baht, formatDate, useAsync, useTableState } from "../../components/ui";
import Icon from "../../components/ui/Icon";

export default function Slips() {
  const { orgId, branchId, toast } = useApp();
  const { tx } = useI18n();
  const tableState = useTableState();

  // สลิปเก่าที่ไม่มี branchId นับทุกสาขา (กติกาเดียวกับแดชบอร์ด)
  const inBranch = (row) => !branchId || !row.branchId || row.branchId === branchId;
  const { data, loading, reload } = useAsync(
    () => slipRepo.list(orgId, { page: tableState.page, perPage: tableState.perPage, search: tableState.search, filter: inBranch }),
    [orgId, branchId, tableState.page, tableState.perPage, tableState.search]
  );
  const { data: all } = useAsync(() => slipRepo.all(orgId, inBranch), [orgId, branchId], []);

  // ตรวจด้วยตนเอง: แอดมินดูสลิปแล้วเลือกใบแจ้งหนี้จริงของร้านที่ตรงกัน (ระบบยังไม่อ่านสลิปให้อัตโนมัติ)
  const [target, setTarget] = useState(null);
  const [invoiceId, setInvoiceId] = useState("");
  const { data: invoices } = useAsync(() => invoiceRepo.all(orgId), [orgId], []);

  const openApprove = (row) => {
    setTarget(row);
    setInvoiceId("");
  };

  // ใบแจ้งหนี้ของลูกค้าคนนี้ที่ยังไม่ชำระมาก่อน
  const candidates = target
    ? (invoices || [])
        .filter((inv) => inv.customerId === target.customerId)
        .sort((a, b) => (a.status === "paid") - (b.status === "paid"))
    : [];

  const approve = async (row, status, invoice = null) => {
    await slipRepo.update(orgId, row.id, {
      status,
      matchedInvoiceNo: status === "verified" ? invoice?.docNo || null : null,
      matchedInvoiceId: status === "verified" ? invoice?.id || null : null,
    });
    setTarget(null);
    reload();
    toast(status === "verified" ? tx("ยืนยันสลิปแล้ว", "Slip verified") : tx("ปฏิเสธสลิปแล้ว", "Slip rejected"), status === "verified" ? "success" : "error");
  };

  const pending = (all || []).filter((row) => row.status === "pending").length;
  const verified = (all || []).filter((row) => row.status === "verified").length;

  return (
    <>
      <PageHead title={tx("ตรวจสอบสลิปโอนเงิน", "Verified slip console")} subtitle={tx("แอดมินตรวจสลิปด้วยตนเอง แล้วยืนยันพร้อมเลือกใบแจ้งหนี้ที่ตรงกัน หรือปฏิเสธ", "Review slips manually, then approve with a matching invoice or reject")} actions={<NotConnected label={tx("ตรวจสลิปอัตโนมัติ", "Auto slip verification")} />} />

      <div className="stat-grid">
        <StatCard icon={<Icon name="checkSquare" />} label={tx("รอตรวจสอบ", "Pending")} value={pending} tone={pending ? "var(--c-amber)" : undefined} />
        <StatCard icon={<Icon name="success" />} label={tx("ยืนยันแล้ว", "Verified")} value={verified} />
        <StatCard icon={<Icon name="file" />} label={tx("สลิปทั้งหมด", "Total slips")} value={(all || []).length} />
      </div>

      <DataTable
        loading={loading}
        rows={data?.rows || []}
        total={data?.total || 0}
        page={tableState.page}
        perPage={tableState.perPage}
        onPage={tableState.setPage}
        onPerPage={tableState.setPerPage}
        emptyText={tx("ยังไม่มีสลิป", "No slips")}
        columns={[
          { key: "customerName", label: tx("ลูกค้า", "Customer") },
          { key: "bank", label: tx("ธนาคาร", "Bank"), render: (row) => <Badge tone="sky">{row.bank}</Badge> },
          { key: "amount", label: tx("จำนวนเงิน", "Amount"), align: "right", render: (row) => baht(row.amount) },
          { key: "transferAt", label: tx("เวลาโอน", "Transferred"), render: (row) => formatDate(row.transferAt, true) },
          { key: "status", label: tx("สถานะ", "Status"), render: (row) => <StatusBadge status={row.status} /> },
          { key: "matched", label: tx("บิลที่ตรงกัน", "Matched invoice"), render: (row) => row.matchedInvoiceNo || row.reason || "-" },
          {
            key: "action",
            label: "",
            render: (row) =>
              row.status === "pending" ? (
                <div className="u-flex u-gap-4">
                  <Button size="sm" variant="success" onClick={() => openApprove(row)}>{tx("ตรวจด้วยตนเอง", "Review manually")}</Button>
                  <Button size="sm" variant="danger" onClick={() => approve(row, "rejected")}>{tx("ปฏิเสธ", "Reject")}</Button>
                </div>
              ) : null,
          },
        ]}
      />

      <Modal
        open={!!target}
        title={tx("ตรวจสลิปด้วยตนเอง", "Review slip manually")}
        onClose={() => setTarget(null)}
        footer={
          <>
            <Button onClick={() => setTarget(null)}>{tx("ยกเลิก", "Cancel")}</Button>
            <Button variant="success" onClick={() => approve(target, "verified", candidates.find((inv) => inv.id === invoiceId))}>{tx("ยืนยันสลิป", "Verify slip")}</Button>
          </>
        }
      >
        {target ? (
          <>
            <div className="u-fs-13 u-mb-8">
              {target.customerName} · {baht(target.amount)} · {formatDate(target.transferAt, true)}
            </div>
            <Select
              label={tx("จับคู่กับใบแจ้งหนี้", "Match with invoice")}
              hint={candidates.length ? undefined : tx("ลูกค้ารายนี้ยังไม่มีใบแจ้งหนี้ในระบบ", "This customer has no invoices")}
              placeholder={tx("ไม่ระบุใบแจ้งหนี้", "No invoice")}
              value={invoiceId}
              onChange={(e) => setInvoiceId(e.target.value)}
              options={candidates.map((inv) => ({ value: inv.id, label: `${inv.docNo} · ${baht(inv.total)} · ${inv.status}` }))}
            />
            <div className="u-fs-12 u-muted">{tx("การยืนยันสลิปจะบันทึกการจับคู่เท่านั้น ไม่ได้ปรับสถานะใบแจ้งหนี้เป็นชำระแล้ว", "Verifying only records the match; the invoice status is not changed")}</div>
          </>
        ) : null}
      </Modal>
    </>
  );
}
